import { Icon, Button as RawButton, color, typography } from '@primer-io/goat'
import ArrowLink from 'components/ArrowLink'
import * as ArticleComponents from 'components/ArticleComponents'
import Clickable from 'components/Clickable'
import Spacer from 'components/Spacer'
import Box from 'ds/Box'
import { H1 } from 'ds/TextGoat'
import React from 'react'
import styled from 'styled-components'

export const Button = props => <RawButton as={Clickable} {...props} style={{ textDecoration: 'none', marginTop: 12 }} />

export const Header = styled.h3`
	${typography('header-small')}
	color: ${color('gray-900')};
`

export const P = styled.p`
	${typography('body-large')}
	color: ${color('gray-700')} !important;
`

export const OverviewSection = styled.div`
	&:not(:first-child) {
		margin-top: 64px;
	}
`

export const HR = styled(ArticleComponents.HR).attrs(() => ({
	style: { margin: '64px 0px 0px 0px', padding: 0 },
}))``

///////////////////////////////////////////
//
///////////////////////////////////////////

export const FullWidthGuideImgContainer = styled.div`
	position: relative;
	min-height: 200px;
	${p => p.theme.above('medium')`
			min-height: 0;
		`}
`

export const FullWidthGuideImg = styled.img`
	position: absolute;
	width: 100%;
	height: 100%;
	border-radius: 4px;
	object-fit: contain;
`

export const FullWidthGuideContent = styled.div`
	padding: 32px;
	& > * + * {
		margin-top: 24px !important;
	}
`

export const FullWidthGuideContainer = styled.div`
	display: flex;
	border: 1px solid ${color('gray-200')};
	border-radius: 4px;

	& > * {
		flex: 1;
	}

	flex-direction: column;
	${p => p.theme.above('medium')`
		flex-direction: ${p.reverse ? 'row-reverse' : 'row'};s

		`}
`

const LearnMoreButtonWrapper = styled.div`
	position: relative;
	left: -10px;
`

const LearnMoreButton = props => (
	<LearnMoreButtonWrapper>
		<ArrowLink icon='ArrowRight' {...props} />
	</LearnMoreButtonWrapper>
)

export const FullWidthGuide = ({ title, description, imgBackground, imgSrc, imgStyle, buttonContent, to, reverse }) => {
	return (
		<FullWidthGuideContainer reverse={reverse}>
			<FullWidthGuideImgContainer style={{ background: imgBackground }}>
				<FullWidthGuideImg src={imgSrc} style={imgStyle} />
			</FullWidthGuideImgContainer>

			<FullWidthGuideContent>
				<Header style={{ paddingTop: 0 }}>{title}</Header>
				<P>{description}</P>

				<LearnMoreButton label={buttonContent} to={to} />
			</FullWidthGuideContent>
		</FullWidthGuideContainer>
	)
}

///////////////////////////////////////////
// OverviewTopSection
///////////////////////////////////////////

const OverviewTopSectionDescription = styled.div`
	${typography('body-large')}

	&  * {
		color: ${color('gray-700')} !important;
	}
`

const OverviewTopSectionImgContainer = styled.div`
	position: relative;
	min-height: 200px;
	${p => p.theme.above('medium')`
			min-height: 160px;
		`}

	border-radius: 4px;
`

export const OverviewTopSection = ({ title, description, imgSrc }) => {
	return (
		<>
			<div style={{ marginTop: 24 }}>
				<H1>{title}</H1>
				<Spacer.V size='32px' />
				<OverviewTopSectionDescription>{description}</OverviewTopSectionDescription>
			</div>

			<HR />
		</>
	)
}

///////////////////////////////////////////
// Guides
///////////////////////////////////////////

const Cover = styled.div`
	display: block;
	aspect-ratio: ${p => p.aspectRatio ?? 2.25};
	margin: -20px -20px 4px;
	background: ${color('gray-200')} center / cover url(${p => p.imgSrc});
`
const GuideLinkTitleContainer = styled.div`
	display: flex;
	align-items: center;
`

const GuideLinkTitle = styled.h4`
	${typography('title-large')}
	color: ${color('gray-900')};
	flex: 1;
`

const StyledBox = styled(Box).attrs(() => ({
	gap: 12,
	padding: 20,
	justify: 'flex-start',
	style: {
		textDecoration: 'none',
	},
}))``

const Tag = styled.div`
	${typography('body-small')}
	color: ${color('gray-900')};
	padding: 2px;
	border-radius: 4px;
	background: ${color('gray-100')};

	margin-left: 8px;
`

export const GuideLink = ({ title, description, link, imgSrc, icon, tag, coverAspectRatio, ...rest }) => (
	<StyledBox as={Clickable} to={link} {...rest}>
		{imgSrc && <Cover imgSrc={imgSrc} aspectRatio={coverAspectRatio} />}
		{icon && <Icon as={icon} style={{ marginBottom: 12 }} color='gray-900' />}
		<GuideLinkTitleContainer>
			<GuideLinkTitle>{title}</GuideLinkTitle>
			{tag && <Tag>{tag}</Tag>}
		</GuideLinkTitleContainer>
		<P>{description}</P>
	</StyledBox>
)

///////////////////////////////////////////
// Small Guides
///////////////////////////////////////////

const SmallGuideLinkRoot = styled(Box).attrs(() => ({
	gap: 12,
	padding: 20,
	justify: 'flex-start',
	style: {
		textDecoration: 'none',
	},
}))`
	background: ${color('gray-50')};
`

const SmallGuideLinkTitle = styled.div`
	${typography('title-large')};
	color: ${color('gray-900')};
`

export const SmallGuideLink = ({ title, icon, to }) => (
	<SmallGuideLinkRoot as={Clickable} to={to}>
		{icon && <Icon as={icon} style={{ marginBottom: 12 }} color='gray-900' />}
		<SmallGuideLinkTitle>{title}</SmallGuideLinkTitle>
	</SmallGuideLinkRoot>
)
